import { Flex } from "@aircall/tractor";
import { useState } from "react";
import { useCall } from "@/hooks/useCall/useCall";
import { useCallDurationRule } from "@/hooks/useCallDurationRule/useCallDurationRule";
import { useSearchConversationContent } from "@/hooks/useSearchConversationContent/useSearchConversationContent";
import { useTranscriptionRule } from "@/hooks/useTranscriptionRules/useTranscriptionRules";
import { ScrollManagerProvider } from "@/providers/ScrollManagerContext/ScrollManagerProvider";
import { useIsFeedbackSent } from "@/states/feedbackSent/feedbackSent";
import { SummarySection } from "../SectionSummary/SectionSummary";
import { TranscriptionWrongCallDuration } from "../TranscriptionEmptyMessage/components/TranscriptionWrongCallDuration/TranscriptionWrongCallDuration";
import { TranscriptionMessage } from "../TranscriptionEmptyMessage/TranscriptionEmptyMessage";
import { TranscriptionFeedbackButtons } from "../TranscriptionFeedbackButtons/TranscriptionFeedbackButtons";
import { TranscriptionList } from "../TranscriptionList/TranscriptionList";
import { TranscriptionLowConfidenceWithTranscript } from "../TranscriptionLowConfidenceWithTranscript/TranscriptionLowConfidenceWithTranscript";
import { TranscriptionNotEnabledMessage } from "../TranscriptionNotEnabledMessage/TranscriptionNotEnabledMessage";
import { TranscriptionNotFound } from "../TranscriptionNotFound/TranscriptionNotFound";
import { TranscriptionReadDuration } from "../TranscriptionReadDuration/TranscriptionReadDuration";
import { TranscriptionProps } from "./Transcription.decl";

export function Transcription({ mobile }: TranscriptionProps) {
  const { call } = useCall();

  const { isFeedbackSent } = useIsFeedbackSent();
  const { shouldDisplayCallDurationScreen } = useCallDurationRule();

  useSearchConversationContent();

  const {
    isTranscriptionDisabled,
    isTranscriptionAvailable,
    isLowConfidenceCall,
    isAvailableCall,
  } = useTranscriptionRule();

  const displayFeedbackButtons = isAvailableCall && !isFeedbackSent;

  const [showLowConfidence, setShowLowConfidence] = useState(
    isTranscriptionAvailable && isLowConfidenceCall
  );
  const handleClick = () => {
    setShowLowConfidence(false);
  };

  if (!call) {
    return null;
  }

  if (isTranscriptionDisabled) {
    return <TranscriptionNotEnabledMessage />;
  }

  if (shouldDisplayCallDurationScreen) {
    return <TranscriptionWrongCallDuration />;
  }

  if (!isTranscriptionAvailable) {
    // No usable transcription. A known reason (low confidence, bad acoustics, no
    // speech) has its own message; otherwise the transcription failed to load or was
    // never generated, so show the generic not-found empty state rather than blank. [CI-6183]
    if (!call.transcriptStatus) {
      return <TranscriptionNotFound />;
    }
    return <TranscriptionMessage status={call.transcriptStatus} />;
  }

  if (showLowConfidence) {
    return <TranscriptionLowConfidenceWithTranscript onClick={handleClick} />;
  }

  return (
    <ScrollManagerProvider mobile={mobile}>
      <Flex alignItems={"center"} mx="32px" mt="40px">
        <Flex flexDirection="column" maxW="640px" gap="l">
          <SummarySection />
          <TranscriptionReadDuration />
          <TranscriptionList />
          {displayFeedbackButtons && <TranscriptionFeedbackButtons />}
        </Flex>
      </Flex>
    </ScrollManagerProvider>
  );
}
